import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    alpha,
    Checkbox,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    ContentCopy as ContentCopyIcon,
    FiberNew as FiberNewIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import { confirmAthleticNetImport } from 'api/AthleticNet';
import type { AthleticNetScrapedDataResponse } from 'api/AthleticNet';

interface AthleticNetEventReviewProps {
    open: boolean;
    onClose: () => void;
    scrapedData: AthleticNetScrapedDataResponse;
    jobId: string;
    individualUuid: string;
    individualName: string;
    onImportConfirmed: (importedCount: number) => void;
}

const AthleticNetEventReview: React.FC<AthleticNetEventReviewProps> = ({
    open,
    onClose,
    scrapedData,
    jobId,
    individualUuid,
    individualName,
    onImportConfirmed,
}) => {
    const theme = useTheme();
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    // Track which events are selected (new events checked by default, duplicates unchecked)
    const [selectedEvents, setSelectedEvents] = useState<Set<number>>(() => {
        const initial = new Set<number>();
        scrapedData.events.forEach((event, index) => {
            if (!event.is_duplicate) {
                initial.add(index);
            }
        });
        return initial;
    });

    // Derived counts
    const selectedCount = selectedEvents.size;
    const newEvents = scrapedData.events.filter((e) => !e.is_duplicate);
    const duplicateEvents = scrapedData.events.filter((e) => e.is_duplicate);

    // Detect name mismatch
    const namesMismatch =
        !!individualName &&
        !!scrapedData.athlete_name &&
        individualName.toLowerCase().trim() !== scrapedData.athlete_name.toLowerCase().trim();

    // Selection handlers
    const handleToggleEvent = (index: number) => {
        setSelectedEvents((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const handleSelectAllNew = () => {
        setSelectedEvents((prev) => {
            const next = new Set(prev);
            scrapedData.events.forEach((event, index) => {
                if (!event.is_duplicate) {
                    next.add(index);
                }
            });
            return next;
        });
    };

    const handleDeselectAll = () => {
        setSelectedEvents(new Set());
    };

    const handleSelectAll = () => {
        setSelectedEvents(new Set(scrapedData.events.map((_, index) => index)));
    };

    // Confirm import
    const handleConfirmImport = async () => {
        if (selectedCount === 0) return;

        try {
            setImporting(true);
            setError(null);

            const eventsToImport = scrapedData.events
                .filter((_, index) => selectedEvents.has(index))
                .map((event) => ({
                    event: event.event,
                    mark: event.mark,
                    date: event.date,
                    meet_date: event.meet_date,
                    meet_name: event.meet_name,
                    location: event.location,
                    result_value: event.result_value,
                    result_unit: event.result_unit,
                }));

            const response = await confirmAthleticNetImport({
                job_id: jobId,
                individual_uuid: individualUuid,
                selected_events: eventsToImport,
            });

            onImportConfirmed(response.data.imported_count);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to import events. Please try again.');
            setImporting(false);
        }
    };

    // Format date for display
    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '--';
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: glassStyle }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            Review Athletic.net Results
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                Importing to:
                            </Typography>
                            <Typography variant="body2" color="primary">
                                {individualName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                |
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                Scraped from:
                            </Typography>
                            <Typography variant="body2" color="info.main">
                                {scrapedData.athlete_name}
                            </Typography>
                        </Box>
                        {individualName &&
                            scrapedData.athlete_name &&
                            individualName.toLowerCase().trim() !==
                                scrapedData.athlete_name.toLowerCase().trim() && (
                                <Alert
                                    severity="error"
                                    icon={<WarningAmberIcon />}
                                    sx={{
                                        mt: 1.5,
                                        py: 1,
                                        px: 2,
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        border: '2px solid',
                                        borderColor: 'error.main',
                                        '& .MuiAlert-icon': {
                                            fontSize: '1.5rem',
                                        },
                                    }}
                                >
                                    NAMES DO NOT MATCH — &quot;{individualName}&quot; vs &quot;
                                    {scrapedData.athlete_name}&quot;. Are you sure this is the right
                                    athlete?
                                </Alert>
                            )}
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Summary chips */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <Chip
                        label={`${scrapedData.total_count} total events`}
                        variant="outlined"
                        size="small"
                    />
                    <Chip
                        icon={<FiberNewIcon />}
                        label={`${newEvents.length} new`}
                        color="success"
                        size="small"
                    />
                    {duplicateEvents.length > 0 && (
                        <Chip
                            icon={<ContentCopyIcon />}
                            label={`${duplicateEvents.length} already imported`}
                            color="default"
                            size="small"
                        />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Chip
                        label={`${selectedCount} selected for import`}
                        color="primary"
                        variant="filled"
                        size="small"
                    />
                </Box>

                {/* Selection controls */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button size="small" variant="outlined" onClick={handleSelectAllNew}>
                        Select All New
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleSelectAll}>
                        Select All
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                        Deselect All
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Event table with Event column on every row */}
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        background: alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        borderRadius: 2,
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{ width: 48 }}>
                                    <Checkbox
                                        size="small"
                                        checked={
                                            selectedCount === scrapedData.events.length &&
                                            scrapedData.events.length > 0
                                        }
                                        indeterminate={
                                            selectedCount > 0 &&
                                            selectedCount < scrapedData.events.length
                                        }
                                        onChange={() => {
                                            if (selectedCount === scrapedData.events.length) {
                                                handleDeselectAll();
                                            } else {
                                                handleSelectAll();
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Meet</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 80 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scrapedData.events.map((event, index) => {
                                const isDuplicate = event.is_duplicate;
                                const isSelected = selectedEvents.has(index);

                                return (
                                    <TableRow
                                        key={index}
                                        hover
                                        onClick={() => handleToggleEvent(index)}
                                        sx={{
                                            cursor: 'pointer',
                                            opacity: isDuplicate && !isSelected ? 0.5 : 1,
                                            transition: 'opacity 0.2s',
                                            '&:last-child td, &:last-child th': { border: 0 },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleToggleEvent(index)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {event.event}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {event.mark}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(event.meet_date || event.date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 280,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {event.meet_name || '--'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {isDuplicate ? (
                                                <Tooltip
                                                    title={`Already imported via ${
                                                        event.existing_source || 'another source'
                                                    }`}
                                                >
                                                    <Chip
                                                        icon={<ContentCopyIcon />}
                                                        label="Duplicate"
                                                        size="small"
                                                        color="default"
                                                        variant="outlined"
                                                        sx={{ fontSize: 11 }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Chip
                                                    icon={<CheckCircleIcon />}
                                                    label="New"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ fontSize: 11 }}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {scrapedData.events.length === 0 && (
                    <Alert severity="info">
                        No track events were found on this Athletic.net profile.
                    </Alert>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    pt: 1,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {selectedCount} of {scrapedData.total_count} event
                    {scrapedData.total_count !== 1 ? 's' : ''} selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} disabled={importing}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={namesMismatch ? 'error' : 'primary'}
                        onClick={handleConfirmImport}
                        disabled={importing || selectedCount === 0}
                        startIcon={
                            importing ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : namesMismatch ? (
                                <WarningAmberIcon />
                            ) : undefined
                        }
                    >
                        {importing
                            ? 'Importing...'
                            : namesMismatch
                            ? 'Names Mismatch — Import Anyway?'
                            : `Import ${selectedCount} Performance${
                                  selectedCount !== 1 ? 's' : ''
                              }`}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default AthleticNetEventReview;
